import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { enUS } from "date-fns/locale/en-US";
import { addDays, differenceInCalendarDays } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { isoToDate } from "../../../utils/activeFiresDateUtils";
import { formatDayMonth } from "../../../utils/activeFiresFormat";
import makeCustomHeader from "../../atoms/activeFiresDatePickerHeader/ActiveFiresDatePickerHeader";
import ActiveFiresTimeline from "../activeFiresTimeline/ActiveFiresTimeline";
import "./ActiveFiresContent.css";
import { useTranslation } from "react-i18next";
registerLocale("fr", fr);
registerLocale("en", enUS);

const MAX_RANGE_DAYS = 7;

// Data is historic-only nothing after today can be picked.
const capToToday = (date, today) => (date > today ? today : date);

// Renders the calendar popup on <body> so the draggable panel can't clip it.
const PopperPortal = ({ children }) => createPortal(children, document.body);

const ActiveFiresContent = ({
  displayStartDate,
  displayEndDate,
  onRangeChange,
  detectedCount,
  isLoading,
  selectedPreset,
  onPreset,
  isSmallScreen,
  isLayerActive,
  timelineHours,
  timelineCursor,
  onTimelineCursorChange,
  timelineBuckets,
  onToggleLayer,
  onCumulativeChange,
}) => {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const [cumulative, setCumulative] = useState(false);

  const dateLocale = i18n.language?.startsWith("fr") ? "fr" : "en";
  const dateFormat = dateLocale === "fr" ? "dd/MM/yyyy" : "MM/dd/yyyy";
  const monthNames = t("common.months", { returnObjects: true });

  const isCustom = selectedPreset === "perso";
  const isNow = selectedPreset === "maintenant";

  const startObj = isoToDate(displayStartDate);
  const endObj = isoToDate(displayEndDate);

  // Timeline visibility depends on the CURRENT mode
  const hasCustomRange = Boolean(startObj && endObj);
  const hasBuckets = (timelineBuckets?.length ?? 0) > 0;
  const showTimeline = isNow
    ? false
    : isCustom
      ? hasCustomRange && hasBuckets // only once a valid range has loaded
      : timelineHours > 0;

  const presets = [
    { key: "maintenant", label: t("activeFires.presets.maintenant") },
    { key: "24h", label: t("activeFires.presets.24h") },
    { key: "72h", label: t("activeFires.presets.72h") },
    { key: "7jours", label: t("activeFires.presets.7jours") },
    {
      key: "perso",
      label: t("activeFires.presets.custom", { defaultValue: "Custom" }),
    },
  ];

  const handleStartChange = (date) => {
    if (!date) {
      onRangeChange(null, endObj);
      return;
    }
    const gap = endObj ? differenceInCalendarDays(endObj, date) : null;
    const keepEnd = gap !== null && gap >= 0 && gap <= MAX_RANGE_DAYS;
    onRangeChange(
      date,
      keepEnd ? endObj : capToToday(addDays(date, MAX_RANGE_DAYS), today),
    );
  };

  // Picking an end keeps the current start if it is still valid, otherwise
  // the start becomes the MAX_RANGE_DAYS before it.
  const handleEndChange = (date) => {
    if (!date) {
      onRangeChange(startObj, null);
      return;
    }
    const gap = startObj ? differenceInCalendarDays(date, startObj) : null;
    const keepStart = gap !== null && gap >= 0 && gap <= MAX_RANGE_DAYS;
    onRangeChange(keepStart ? startObj : addDays(date, -MAX_RANGE_DAYS), date);
  };

  const pickerCommon = {
    locale: dateLocale,
    dateFormat,
    className: "afp-date-input",
    calendarClassName: "afp-calendar",
    wrapperClassName: "w-100",
    isClearable: true,
    maxDate: today,
    ...(isSmallScreen
      ? { withPortal: true, portalId: "afp-datepicker-portal" }
      : {
          // Render on <body> with fixed positioning so the draggable panel's
          // overflow can't clip the calendar.
          popperContainer: PopperPortal,
          popperPlacement: "bottom-start",
          popperProps: { strategy: "fixed" },
        }),
  };

  /* ---------- stats ---------- */
  const { total, atTime } = useMemo(() => {
    const counts = (timelineBuckets || []).map((b) => b.features?.length ?? 0);
    const sum = counts.reduce((a, c) => a + c, 0);
    if (counts.length === 0) return { total: 0, atTime: 0 };
    const idx = Math.min(Math.max(timelineCursor ?? 0, 0), counts.length - 1);
    const current = cumulative
      ? counts.slice(0, idx + 1).reduce((a, c) => a + c, 0)
      : counts[idx];
    return { total: sum, atTime: current };
  }, [timelineBuckets, timelineCursor, cumulative]);

  const handleCumulativeChange = (value) => {
    setCumulative(value);
    onCumulativeChange?.(value);
  };

  /* ---------- range summary ---------- */
  const first = timelineBuckets?.[0]?.timestamp;
  const last = timelineBuckets?.[timelineBuckets.length - 1]?.timestamp;
  const dayCount =
    first && last ? Math.max(1, differenceInCalendarDays(last, first)) : 0;

  return (
    <div className="afp-content">
      <div className="afp-seg" role="tablist">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={selectedPreset === p.key}
            className={`afp-seg-btn ${selectedPreset === p.key ? "active" : ""}`}
            onClick={() => onPreset(p.key)}
            data-testid={`afp-preset-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom range: start + end */}
      {isCustom && (
        <>
          <p className="afp-custom-hint">
            {t("activeFires.custom.hint", {
              count: MAX_RANGE_DAYS,
              defaultValue: "Pick a start and an end date · {{count}} days max",
            })}
          </p>
          <div className="afp-dates">
            <div className="afp-date-field">
              <span className="afp-field-label">
                {t("activeFires.dateRange.startLabel", { defaultValue: "From" })}
              </span>
              <DatePicker
                {...pickerCommon}
                selected={startObj}
                onChange={handleStartChange}
                placeholderText={t("activeFires.dateRange.startPlaceholder")}
                renderCustomHeader={makeCustomHeader(
                  (date) => handleStartChange(date),
                  monthNames,
                )}
              />
            </div>
            <div className="afp-date-field">
              <span className="afp-field-label">
                {t("activeFires.dateRange.endLabel", { defaultValue: "To" })}
              </span>
              <DatePicker
                {...pickerCommon}
                selected={endObj}
                onChange={handleEndChange}
                placeholderText={t("activeFires.dateRange.endPlaceholder", {
                  defaultValue: "End date",
                })}
                renderCustomHeader={makeCustomHeader(
                  (date) => handleEndChange(date),
                  monthNames,
                )}
              />
            </div>
          </div>
        </>
      )}

      {isLoading && (
        <p className="afp-loading">{t("activeFires.loading")}</p>
      )}

      {/* "Now": a single live count, no replay */}
    {isNow && (
  <div className="afp-stats afp-stats-single">
    <div className="afp-stat">
      <span className="afp-stat-label">
        {t("activeFires.stats.now", { defaultValue: "Detected now" })}
      </span>
      <span className="afp-stat-value" data-testid="afp-stat-now">
        {isLoading
          ? "–"
          : detectedCount > 0
            ? detectedCount
            : t("activeFires.stats.noPoints", {
                defaultValue: "No hot spots",
              })}
      </span>
    </div>
  </div>
)}

      {/* Range summary + stats + replay */}
      {showTimeline && (
        <>
          {/* {first && last && (
            <p className="afp-summary">
              <strong>
                {formatDayMonth(first, i18n.language)} →{" "}
                {formatDayMonth(last, i18n.language)}
              </strong>{" "}
              ·{" "}
              {t("activeFires.range.days", {
                count: dayCount,
                defaultValue_one: "{{count}} day",
                defaultValue_other: "{{count}} days",
              })}
            </p>
          )} */}

          <div className="afp-stats">
            <div className="afp-stat">
              <span className="afp-stat-label">
                {cumulative
                  ? t("activeFires.stats.upToTime", {
                      defaultValue: "Up to this time",
                    })
                  : t("activeFires.stats.atTime", {
                      defaultValue: "At this time",
                    })}
              </span>
              <span className="afp-stat-value" data-testid="afp-stat-at-time">
                {atTime}
              </span>
            </div>
            <div className="afp-stat">
              <span className="afp-stat-label">
                {t("activeFires.stats.wholePeriod", {
                  defaultValue: "Whole period",
                })}
              </span>
              <span className="afp-stat-value" data-testid="afp-stat-total">
                {total}
              </span>
            </div>
          </div>

          <ActiveFiresTimeline
            cursor={timelineCursor}
            onCursorChange={onTimelineCursorChange}
            buckets={timelineBuckets}
            onToggleLayer={onToggleLayer}
            isLayerActive={isLayerActive}
            cumulative={cumulative}
            onCumulativeChange={handleCumulativeChange}
          />
        </>
      )}
    </div>
  );
};

export default React.memo(ActiveFiresContent);